"use client";

import { useCallback, useId, useMemo } from "react";
import { useNoteLabel } from "@/hooks/use-notation";
import { useAppPiano } from "@/hooks/journey/use-app-piano";
import { useFluentStreak } from "@/hooks/journey/use-fluent-streak";
import { usePracticeRun } from "@/hooks/journey/use-practice-run";
import { AUDITION_TEMPO_FRACTION } from "@/lib/learning-journey/thresholds";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
} from "@/lib/learning-journey/step-mapping";
import type { Phrase, PhraseFSMSnapshot } from "@/lib/learning-journey/types";
import type { AnalysisChord } from "@/lib/music/analyzer-types";
import { noteLabellers } from "../grip";
import {
  auditionSchedule,
  buildPracticeGroups,
  moveGroupIndices,
} from "../practice";
import { FloatingHeader } from "../shell/floating-header";
import { FloatingShell } from "../shell/floating-shell";
import { MiniSteps } from "../shell/mini-steps";
import { PassButton, SkipHandButton } from "../shell/skip-hand-button";
import {
  PANEL_PADDING_X_PX,
  PANEL_WIDTH_PX,
} from "../shell/panel-metrics";
import {
  fluencyLine,
  hesitationLine,
  isFluent,
  practiceTitle,
  readablePass,
  readyForTime,
  readyForTimeLine,
  stumbleLine,
} from "../mastery";
import {
  AuditionButton,
  CompletedFeedback,
  PASS_DOTS,
  Progress,
  RetrySecondaryButton,
} from "../shell/run-controls";
import { SequenceWatcher } from "../sequence-watcher";

/**
 * View for `S1b` (left-hand practice) and `S2b` (right-hand practice): the
 * note-by-note run of **one** hand, without rhythm.
 *
 * The both-hands run used to be a third variant of this file. It is not this
 * screen with `hand = both`: every organ here has a single referent — the
 * grips of *the* hand, where *the* hand moves, the configuration *the* hand
 * was just given — and the both-hands step has no single referent for any of
 * them. It lived here as a cascade of `null`s that switched three features
 * off rather than replacing them. See `both-hands-state.tsx`.
 *
 * ## Validation policy
 *
 * V1: validate on completion, no fluency gate. Speed belongs to S4 and
 * chaining to S5; a speed gate here would punish the deliberate chord-taking
 * that the grip step explicitly protects, at the step the player returns to
 * most often. The acceptance rule lives in `SequenceMatcher`, and it is the
 * grip step's: the keys have to be **down together**, with no time window.
 *
 * ## Repetition is the point, so it is counted
 *
 * Finishing the sequence does not advance the journey — motor consolidation
 * wants five to twenty repetitions of a chunk, so the run ends on an offer to
 * play it again and moving on is the deliberate second choice. The run length
 * is **not** capped here: measured over PDMX 450, the median single-hand run
 * is 8 groups and 96.9% are 20 or fewer, because `work-units.ts` already did
 * the dosing upstream.
 *
 * ## What this step teaches, and what it hands back
 *
 * The grip step installed configurations; this one makes them move. 76.9% of
 * left-hand runs open inside a place the player was just made to take, so the
 * run says so, and carries the fingering on the keys a grip certifies. The
 * grip is a landmark here and never a unit — see `practice.ts`.
 */

/** The order at the top of the panel. Same scale as the grip step's. */
const HERO_FONT_PX = 38;

const EMPTY_KEYS: readonly string[] = [];

type PracticeHandSide = "left" | "right";

type PracticeStateProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly journey: LearningJourney;
  /** The hand this run practises. */
  readonly hand: PracticeHandSide;
  /** Analyzer chords, used only to frame the keyboard as S0 and S1a framed it. */
  readonly chords: readonly AnalysisChord[];
  /** Real time of a tick, for the audition. */
  readonly tickToSeconds: (tick: number) => number;
  readonly phraseLabel?: string;
  /**
   * Called when the player has finished the sequence. Always `'validated'`
   * in V1, we keep the parameter for API compatibility with the FSM.
   */
  readonly onCompleted: (outcome: "validated" | "failed") => void;
};

export function PracticeState({
  phrase,
  phraseSnapshot,
  journey,
  hand,
  tickToSeconds,
  phraseLabel,
  onCompleted,
}: PracticeStateProps) {
  const titleId = useId();
  const toNoteLabel = useNoteLabel();
  const labels = useMemo(() => noteLabellers(toNoteLabel), [toNoteLabel]);

  const variant = hand === "left" ? "S1b" : "S2b";
  const groups = useMemo(
    () => buildPracticeGroups(phrase, variant),
    [phrase, variant],
  );
  const grips = phrase.gripsByHand[hand];
  const moves = useMemo(
    () => moveGroupIndices(groups, grips),
    [groups, grips],
  );

  const run = usePracticeRun(phrase, groups, labels.key);

  const audition = useAppPiano();
  // This hand alone, which is what is being built here. S0 already played the
  // unit whole, by itself, two screens ago. See `auditionSchedule`.
  const auditionNotes = useMemo(
    () =>
      auditionSchedule(
        phrase,
        tickToSeconds,
        AUDITION_TEMPO_FRACTION,
        hand === "left" ? "L" : "R",
      ),
    [phrase, tickToSeconds, hand],
  );
  const toggleAudition = useCallback(() => {
    if (audition.playing) {
      audition.stop();

      return;
    }

    audition.play(auditionNotes);
  }, [audition, auditionNotes]);

  /**
   * On most units this is « the next step ». On a unit where only one hand
   * plays it is **the régime switch**: there is no both-hands run, so this
   * screen is the free régime of the step that meets the metronome, and
   * `canEnterTime` says so. See `PhraseFSM`, « S3 and S4 are one step in two
   * régimes ».
   *
   * No tempo is handed over, here or before: `naturalRate` is measured where
   * both hands are playing, and on this screen only one is. The run in time
   * opens on the density rule, as it always has for these units.
   */
  /**
   * Whether this pass says anything about the unit: not one the player
   * resumed part-way through, which is a piece of the passage and not the
   * passage. There is no accompanied pass on a single hand.
   */
  const speaks = run.resumeIndex === 0;

  /** Fluent passes in a row. See `useFluentStreak`. */
  const fluent = useFluentStreak(run.lastPass, run.abandons, speaks);

  /**
   * Which of the two acts the card puts forward.
   *
   * On an ordinary unit « Continuer » is the other hand, and the pass count
   * is the right rule: the question the card is asking is how many
   * repetitions this hand is worth, and four is the dosage.
   *
   * On a unit where only one hand plays, this run **is** the free régime of
   * the step that meets the metronome, and the question is a different one.
   * The reading answers it — see `both-hands-state`, which makes the same
   * arbitration with the same words.
   *
   * `readyForTime` alone, with no tempo condition: `naturalRate` needs the
   * unit's density and this screen is not given `bpm`/`ppq`, so a one-handed
   * unit has never handed a tempo over and opens on the density rule. That is
   * unchanged, and worth fixing on its own one day.
   */
  const readable = run.lastPass !== null && readablePass(run.lastPass);
  const leadsToTime = phraseSnapshot.canEnterTime
    ? readyForTime(fluent) || (!readable && run.passes >= PASS_DOTS)
    : undefined;

  /** One line under the headline. See `both-hands-state` for the order. */
  const verdict: string | null =
    run.lastPass === null
      ? null
      : phraseSnapshot.canEnterTime && readyForTime(fluent)
        ? readyForTimeLine(null)
        : isFluent(run.lastPass)
          ? (phraseSnapshot.canEnterTime ? fluencyLine(fluent) : null)
          : (hesitationLine(run.lastPass) ?? stumbleLine(run.lastPass));

  // No run is recorded: see `PhraseFSMSnapshot.handSkipTarget`.
  const handleSkip = useCallback(() => {
    journey.skipHand();
  }, [journey]);

  const handleContinue = useCallback(() => {
    // Guarded because the switch is drawn before the first pass too, and a
    // `practiceRunCompleted` for a run that never happened would put a
    // phantom repetition in the unit's history.
    if (run.completed) {
      onCompleted("validated");
    }

    if (phraseSnapshot.canEnterTime) {
      journey.enterTime();
    }
  }, [journey, onCompleted, phraseSnapshot.canEnterTime, run.completed]);

  const stepIndex = phraseStateToStepIndex(phraseSnapshot.state);
  // What this unit's stepper counts: the steps it actually has. A
  // right-hand-only unit has three, not five with two dead slots.
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );

  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handleBack = useCallback(() => {
    journey.stepBack();
  }, [journey]);

  const handColor =
    hand === "left" ? "var(--color-hand-left)" : "var(--color-hand-right)";

  /**
   * The band takes over once the player is playing.
   *
   * Two heights, the same rule the run in time already follows: a full panel
   * while nothing moves and the player is reading, a line across the roll
   * once the roll is the screen. The threshold is the first key struck —
   * before it the panel still owes the passage's opening line and the offer
   * to hear it, after it the only things worth a pixel are where you are and
   * how to get out.
   */
  /**
   * The keys are read here, outside the two shells.
   *
   * It used to sit inside the panel, which was harmless while there was one
   * shell. There are two now, and the band returning early took the watcher
   * down with it: the first key struck collapsed the panel, and the collapse
   * unmounted the very thing listening for the second. Nothing is read from
   * a shell, so nothing about reading belongs inside one.
   */
  const announcement = (
    <div aria-live="polite" className="sr-only">
      {run.completed
        ? `Séquence terminée, passage ${run.passes}.`
        : run.currentGroup
          ? `Groupe ${run.position} sur ${run.total} : ${run.currentGroup.midis
              .map(labels.key)
              .join(", ")}.`
          : ""}
    </div>
  );

  const watcher = (
    <SequenceWatcher
      key={run.runId}
      groups={run.sequenceGroups}
      startIndex={run.resumeIndex}
      onGroupConfirmed={run.onGroupConfirmed}
      onSequenceCompleted={run.onSequenceCompleted}
      onError={run.onError}
      onCurrentIndexChange={run.onCurrentIndexChange}
    />
  );

  if (!run.completed && run.currentIndex > 0) {
    return (
      <>
        {watcher}
        {announcement}
      <FloatingShell
        variant="band"
        ariaLabel={ariaLabel(hand, phraseLabel)}
        titleId={titleId}
        contentKey="practice-band"
      >
        <PracticeBand
          hand={hand}
          titleId={titleId}
          onRetry={run.onRetry}
          keys={
            run.currentGroup === undefined
              ? EMPTY_KEYS
              : run.currentGroup.midis.map(labels.key)
          }
          position={run.position}
          total={run.total}
          percent={run.percent}
          markers={moves}
          handColor={handColor}
          phraseLabel={phraseLabel}
        />
      </FloatingShell>
      </>
    );
  }

  return (
    <>
      {watcher}
      {announcement}
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel={ariaLabel(hand, phraseLabel)}
      titleId={titleId}
    >
      <FloatingHeader
        stepIndex={stepPosition(steps, stepIndex)}
        stepTotal={steps.length}
        // The hand is said once, above the order, as on the grip step.
        kicker="PRATIQUE"
        titleId={titleId}
      />

      <div style={{ padding: `26px ${PANEL_PADDING_X_PX}px 20px` }}>
        {/*
          All of this belongs to a run that is still going: kept at the end,
          it would open the card on an order for a pass nobody is about to
          make.
        */}
        {!run.completed && (
          <>
            <p
              className="font-mono"
              style={{
                margin: 0,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: handColor,
              }}
            >
              {hand === "left" ? "Main gauche" : "Main droite"}
            </p>

            {/*
              Nothing but the go. The finger and the key were the order one
              screen ago, and the hand is on them: saying them again read as
              being asked to do it twice.
            */}
            <p
              style={{
                margin: "10px 0 0",
                fontSize: HERO_FONT_PX,
                lineHeight: 1.08,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--color-text-primary)",
              }}
            >
              À toi de jouer
            </p>

            <div
              aria-live="polite"
              style={{
                // No reservation any more: it existed so a diagnosis
                // appearing would not shove the *keyboard* down, and the
                // keyboard moved to the roll. Held open, it was a blank
                // 32 px in the middle of a panel that already had too much
                // of it.
                fontSize: 12,
                lineHeight: 1.4,
                color: "var(--color-text-secondary)",
                textWrap: "pretty",
              }}
            >
              {run.diagnosis}
            </div>
          </>
        )}

        {!run.completed && run.currentIndex === 0 && (
          <div
            className="flex items-center"
            style={{
              marginTop: 8,
              marginLeft: -12,
              marginRight: -12,
              justifyContent: "space-between",
            }}
          >
            {run.passes === 0 ? (
              <AuditionButton
                playing={audition.playing}
                onToggle={toggleAudition}
                what={hand === "left" ? "la main gauche" : "la main droite"}
              />
            ) : (
              <span />
            )}

            {/*
              Moving on is the player's call, before any pass too. On a unit
              where only this hand plays, this run is the free régime of the
              step in time, and the way on is the metronome.
            */}
            {phraseSnapshot.canEnterTime ? (
              <PassButton label="Passer en rythme" onClick={handleContinue} />
            ) : (
              <SkipHandButton
                target={phraseSnapshot.handSkipTarget}
                onSkip={handleSkip}
              />
            )}
          </div>
        )}

        {run.completed && (
          <CompletedFeedback
            passes={run.passes}
            onRetry={run.onRetry}
            onContinue={handleContinue}
            handColor={handColor}
            // Only on a unit where this run *is* the free régime. Everywhere
            // else « Continuer » leads to the other hand, and saying « passer
            // en rythme » would name a screen two steps away.
            continueLabel={
              phraseSnapshot.canEnterTime ? "Passer en rythme →" : undefined
            }
            ready={leadsToTime}
            // « Passage réussi » on a pass spent hunting for keys was a
            // tautology dressed as praise: the watcher waits for the right
            // key, so every pass that ends walked every attack.
            title={speaks ? practiceTitle(run.lastPass) : undefined}
            note={speaks ? (verdict ?? undefined) : undefined}
            audition={
              <AuditionButton
                playing={audition.playing}
                onToggle={toggleAudition}
                what={hand === "left" ? "la main gauche" : "la main droite"}
              />
            }
          />
        )}

      </div>

      <MiniSteps
        active={stepIndex}
        steps={steps}
        reachable={reachable}
        onJump={handleJump}
        onBack={phraseSnapshot.canStepBack ? handleBack : undefined}
        label={run.total > 0 ? `${run.position} / ${run.total}` : undefined}
      />
    </FloatingShell>
    </>
  );
}

/**
 * The practice step, reduced to a line across the top of the roll.
 *
 * What survives the reduction, and why. **The keys to play**, because they
 * are the one thing the roll cannot say: it shows where a note sits and
 * which finger takes it, and it never names it — on a product with no score,
 * the name is only ever spoken in the panel the player just closed. So the
 * band names it, in the hand's colour, at the size of the thing you look at.
 *
 * **Where you are** (`6 / 13`), which the roll cannot say either: it shows
 * the passage, not how far into it you have walked. **Starting over**, the
 * one act wanted mid-walk. **The phrase**, because the chip that normally
 * carries it lives exactly here and stands down for the band.
 *
 * What went. The stepper: `‹ Retour 3/5` next to `6 / 13` put two counters
 * of different kinds side by side, and neither could be read as itself.
 * Starting over is the way back — it folds the band into the panel, which
 * has the stepper — so the band is not a screen without an exit.
 */
function PracticeBand({
  hand,
  titleId,
  onRetry,
  keys,
  position,
  total,
  percent,
  markers,
  handColor,
  phraseLabel,
}: {
  hand: "left" | "right";
  titleId: string;
  onRetry: () => void;
  /** The keys of the current group, named. */
  keys: readonly string[];
  position: number;
  total: number;
  percent: number;
  markers: ReadonlySet<number>;
  handColor: string;
  phraseLabel?: string;
}) {
  return (
    <div
      className="flex items-center"
      style={{ gap: 12, padding: `10px ${PANEL_PADDING_X_PX}px` }}
    >
      {/*
        Both flanks take `flex: 1 1 0`, which is what actually centres the
        keys: whatever either side holds, the middle sits on the axis of the
        roll. Absolute positioning would have centred them too, and would
        have let them slide under the flanks on a narrow viewport.
      */}
      <div
        className="flex items-center"
        style={{ gap: 16, flex: "1 1 0", minWidth: 0 }}
      >
        <span
          id={titleId}
          className="font-mono shrink-0"
          style={{
            fontSize: 11,
            letterSpacing: "0.12em",
            color: "var(--color-text-secondary)",
          }}
        >
          {hand === "left" ? "MAIN GAUCHE" : "MAIN DROITE"}
        </span>

        <span
          aria-hidden
          style={{
            width: 1,
            height: 14,
            background: "var(--color-border-subtle)",
          }}
        />

        {/*
          Where the opening line used to be, and it is a better tenant. The
          line said "remets ta main en place", which the panel had said one
          screen earlier and which nothing had changed since — a sentence
          taking the width of the roll to repeat itself. Starting the passage
          over is the one act a player wants mid-walk, and it had nowhere to
          live once the panel folded.
        */}
        <RetrySecondaryButton onRetry={onRetry} compact />
      </div>

      <div
        className="flex items-baseline justify-center shrink-0"
        style={{ gap: 10 }}
      >
        {keys.map((key) => (
          <span
            key={key}
            className="font-mono"
            style={{
              fontSize: 21,
              lineHeight: 1,
              letterSpacing: "0.02em",
              color: handColor,
            }}
          >
            {key}
          </span>
        ))}
      </div>

      <div
        className="flex items-center justify-end"
        style={{ gap: 16, flex: "1 1 0", minWidth: 0 }}
      >
        <span style={{ width: 120, flexShrink: 0 }}>
          <Progress
            percent={percent}
            markers={markers}
            total={total}
            color={handColor}
            compact
          />
        </span>

        {total > 0 && (
          <span
            className="font-mono tabular-nums shrink-0"
            style={{ fontSize: 11, color: "var(--color-text-secondary)" }}
          >
            {position} / {total}
          </span>
        )}

        {phraseLabel !== undefined && phraseLabel !== "" && (
          <span
            className="font-mono shrink-0"
            style={{ fontSize: 11, color: "var(--color-text-muted)" }}
          >
            {phraseLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function ariaLabel(
  hand: PracticeHandSide,
  phraseLabel: string | undefined,
): string {
  const base =
    hand === "left" ? "Pratique main gauche" : "Pratique main droite";

  if (phraseLabel === undefined || phraseLabel === "") {
    return base;
  }

  return `${base}, ${phraseLabel}`;
}
